import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Storefront,
  MagnifyingGlass,
  ArrowClockwise,
  SignOut,
  CaretUp,
  CaretDown,
  CaretLeft,
  CaretRight,
  CircleNotch,
  WarningCircle,
  ArrowSquareOut,
  X,
  FilePdf,
  FileCsv,
  DownloadSimple,
  FileText,
} from "@phosphor-icons/react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { dealerApi, getErrorMessage, DEALER_TOKEN_KEY, API } from "@/lib/api";
import { useDealerAuth } from "@/context/DealerAuthContext";
import { OtmLogo } from "@/components/common/OtmLogo";
import { usePolling } from "@/hooks/usePolling";

const CLAIM_STATUS_STYLES = {
  "approved": "bg-[#73bf44]/15 text-[#73bf44] border-[#73bf44]/30",
  "completed": "bg-teal-500/15 text-teal-300 border-teal-500/30",
  "invoiced": "bg-teal-500/15 text-teal-300 border-teal-500/30",
  "declined": "bg-red-500/15 text-red-400 border-red-500/30",
  "further information required": "bg-orange-500/15 text-orange-400 border-orange-500/30",
  "pending assessment": "bg-neutral-500/15 text-neutral-300 border-neutral-500/40",
};

const fileViewUrl = (f) => `${API}/dealer/file/${f.asset_id}?token=${encodeURIComponent(f.token)}`;
const fileDownloadUrl = (f) => `${fileViewUrl(f)}&dl=1`;

function fmtDate(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return iso || "";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
}

function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

function Cell({ col, value }) {
  if (!value) return <span className="text-neutral-600">—</span>;
  if (col.type === "date") return <span>{fmtDate(value)}</span>;
  if (col.type === "status") {
    return <StatusPill value={value} colors={col.status_colors} />;
  }
  if (col.type === "email") return <span className="text-neutral-300">{value}</span>;
  return <span>{value}</span>;
}

function statusStyle(v) {
  return CLAIM_STATUS_STYLES[(v || "").toLowerCase()] || "bg-neutral-800/70 text-neutral-200 border-neutral-700";
}

function StatusPill({ value, colors }) {
  if (!value) return <span className="text-neutral-600">—</span>;
  const hex = colors && colors[value];
  if (hex) {
    return (
      <span
        className="inline-block text-xs font-semibold px-2.5 py-1 rounded-full border whitespace-nowrap"
        style={{ color: hex, backgroundColor: `${hex}22`, borderColor: `${hex}66` }}
      >
        {value}
      </span>
    );
  }
  return <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full border ${statusStyle(value)}`}>{value}</span>;
}

const cellText = (col, row) => {
  const v = row.values?.[col.id] || "";
  return col.type === "date" ? fmtDate(v) : v;
};

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

function exportCsv(columns, rows, baseName) {
  const esc = (s) => {
    s = String(s ?? "");
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [columns.map((c) => c.title)];
  rows.forEach((r) => lines.push(columns.map((c) => cellText(c, r))));
  const csv = "\ufeff" + lines.map((row) => row.map(esc).join(",")).join("\r\n");
  downloadBlob(new Blob([csv], { type: "text/csv;charset=utf-8;" }), `${baseName}.csv`);
}

function exportPdf(columns, rows, title, baseName) {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  doc.setFontSize(14);
  doc.text(title, 40, 36);
  doc.setFontSize(9);
  doc.setTextColor(120);
  doc.text(`${rows.length} claim${rows.length === 1 ? "" : "s"}  ·  ${new Date().toLocaleString()}`, 40, 52);
  autoTable(doc, {
    startY: 64,
    head: [columns.map((c) => c.title)],
    body: rows.map((r) => columns.map((c) => cellText(c, r))),
    styles: { fontSize: 8, cellPadding: 4, overflow: "linebreak" },
    headStyles: { fillColor: [115, 191, 68], textColor: [5, 5, 5], fontStyle: "bold" },
    alternateRowStyles: { fillColor: [245, 247, 243] },
    margin: { left: 40, right: 40 },
  });
  doc.save(`${baseName}.pdf`);
}

function Lightbox({ files, index, onClose }) {
  const [i, setI] = useState(index);
  const touchX = useRef(null);
  const go = useCallback((d) => setI((p) => (p + d + files.length) % files.length), [files.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, onClose]);

  const f = files[i];
  const multi = files.length > 1;

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/95 flex flex-col"
      data-testid="photo-lightbox"
      onClick={onClose}
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        const dx = e.changedTouches[0].clientX - (touchX.current ?? 0);
        if (multi && Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
      }}
    >
      <div className="flex items-center justify-between px-5 py-4 text-neutral-300" onClick={(e) => e.stopPropagation()}>
        <span className="text-sm truncate max-w-[50%]" title={f.name}>{f.name}</span>
        <div className="flex items-center gap-3">
          {multi && <span className="text-sm text-neutral-400" data-testid="lightbox-counter">{i + 1} / {files.length}</span>}
          <a href={fileDownloadUrl(f)} data-testid="lightbox-download" className="inline-flex items-center gap-1.5 text-xs bg-[#73bf44] text-[#050505] font-semibold rounded-full px-3 py-1.5 hover:bg-[#5fa338] transition-colors">
            <DownloadSimple size={14} /> Download
          </a>
          <button onClick={onClose} data-testid="lightbox-close" className="text-neutral-300 hover:text-white"><X size={22} /></button>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 pb-6 relative" onClick={(e) => e.stopPropagation()}>
        {multi && (
          <button onClick={() => go(-1)} data-testid="lightbox-prev" className="absolute left-3 sm:left-6 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors">
            <CaretLeft size={26} weight="bold" />
          </button>
        )}
        <img src={fileViewUrl(f)} alt={f.name} className="max-h-[82vh] max-w-[90vw] object-contain rounded-lg" data-testid="lightbox-image" />
        {multi && (
          <button onClick={() => go(1)} data-testid="lightbox-next" className="absolute right-3 sm:right-6 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors">
            <CaretRight size={26} weight="bold" />
          </button>
        )}
      </div>
    </div>
  );
}

function FileBlock({ file }) {
  const Icon = file.is_pdf ? FilePdf : FileText;
  return (
    <div className="border border-neutral-800 rounded-lg overflow-hidden" data-testid="detail-file">
      <div className="flex items-center gap-3 px-4 py-3 bg-black/30">
        <Icon size={22} className="text-[#73bf44] shrink-0" weight="duotone" />
        <span className="text-sm text-white truncate flex-1" title={file.name}>{file.name}</span>
        <a href={fileViewUrl(file)} target="_blank" rel="noreferrer" data-testid="detail-file-open" className="inline-flex items-center gap-1.5 text-xs border border-neutral-700 rounded-full px-3 py-1.5 text-neutral-200 hover:border-[#73bf44] hover:text-[#73bf44] transition-colors">
          <ArrowSquareOut size={13} /> View
        </a>
        <a href={fileDownloadUrl(file)} data-testid="detail-file-download" className="inline-flex items-center gap-1.5 text-xs bg-[#73bf44] text-[#050505] font-semibold rounded-full px-3 py-1.5 hover:bg-[#5fa338] transition-colors">
          <DownloadSimple size={13} /> Download
        </a>
      </div>
      {file.is_pdf && (
        <iframe src={fileViewUrl(file)} title={file.name} className="w-full h-[460px] bg-white" data-testid="detail-pdf-frame" />
      )}
    </div>
  );
}

function FieldItem({ field, colors }) {
  const isLong = field.type === "long_text";
  const isLink = field.type === "link" || field.type === "email";
  return (
    <div className={isLong ? "sm:col-span-2" : ""}>
      <div className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-neutral-500 mb-1">{field.title}</div>
      {field.type === "status" ? (
        <StatusPill value={field.value} colors={colors} />
      ) : field.type === "date" ? (
        <div className="text-sm text-white">{fmtDate(field.value)}</div>
      ) : isLink ? (
        <a href={field.type === "email" ? `mailto:${field.value}` : field.value} target="_blank" rel="noreferrer" className="text-sm text-[#73bf44] hover:underline break-all">{field.value}</a>
      ) : (
        <div className={`text-sm text-white ${isLong ? "whitespace-pre-wrap leading-relaxed" : ""} break-words`}>{field.value}</div>
      )}
    </div>
  );
}

function ClaimDetailModal({ id, onClose, statusColors }) {
  const [state, setState] = useState({ loading: true, error: "", detail: null });
  const [lightbox, setLightbox] = useState(null); // { files, index }

  useEffect(() => {
    let alive = true;
    setState({ loading: true, error: "", detail: null });
    dealerApi
      .get(`/dealer/claims/${id}`, { timeout: 40000 })
      .then(({ data }) => { if (alive) setState({ loading: false, error: "", detail: data }); })
      .catch((err) => { if (alive) setState({ loading: false, error: getErrorMessage(err, "Couldn't load this claim."), detail: null }); });
    return () => { alive = false; };
  }, [id]);

  const d = state.detail;
  const chassis = d?.fields?.find((f) => f.title === "OTM Chassis Number")?.value || d?.name || "Claim";
  const status = d?.fields?.find((f) => f.title === "Claim Status")?.value;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-[#0F0F0F] border border-neutral-800 rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="dealer-detail-modal">
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-neutral-800 sticky top-0 bg-[#0F0F0F] z-10">
          <div className="flex items-center gap-3 min-w-0">
            <h3 className="font-heading font-bold text-white truncate">{chassis}</h3>
            {status && <StatusPill value={status} colors={statusColors?.["Claim Status"]} />}
          </div>
          <button onClick={onClose} data-testid="dealer-detail-close" className="text-neutral-500 hover:text-white shrink-0"><X size={18} /></button>
        </div>

        <div className="p-6">
          {state.loading ? (
            <div className="flex items-center gap-2 text-neutral-500 py-16 justify-center"><CircleNotch size={18} className="animate-spin" /> Loading claim…</div>
          ) : state.error ? (
            <div className="flex flex-col items-center gap-2 text-neutral-400 py-12"><WarningCircle size={26} className="text-red-400" /><p className="text-sm">{state.error}</p></div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                {d.fields.map((f, i) => <FieldItem key={i} field={f} colors={statusColors?.[f.title]} />)}
              </div>
              {d.file_groups.filter((g) => g.files.length > 0).map((g) => {
                const imgs = g.files.filter((f) => f.is_image);
                const docs = g.files.filter((f) => !f.is_image);
                return (
                  <div key={g.column_id} className="mt-7" data-testid={`detail-group-${g.column_id}`}>
                    <h4 className="text-[0.7rem] font-bold uppercase tracking-[0.15em] text-[#73bf44] mb-3">{g.title}</h4>
                    {docs.length > 0 && (
                      <div className="space-y-3 mb-3">
                        {docs.map((f, i) => <FileBlock key={i} file={f} />)}
                      </div>
                    )}
                    {imgs.length > 0 && (
                      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                        {imgs.map((f, i) => (
                          <button
                            key={i}
                            onClick={() => setLightbox({ files: imgs, index: i })}
                            data-testid="detail-photo"
                            className="group block rounded-lg overflow-hidden border border-neutral-800 aspect-square bg-black/40 focus:outline-none focus:ring-2 focus:ring-[#73bf44]"
                          >
                            <img src={fileViewUrl(f)} alt={f.name} className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-300" loading="lazy" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
      {lightbox && <Lightbox files={lightbox.files} index={lightbox.index} onClose={() => setLightbox(null)} />}
    </div>
  );
}

export default function DealerBoard() {
  const { isAuthed, logout } = useDealerAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [data, setData] = useState({ columns: [], rows: [], fetched_at: null });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState({ id: null, dir: "desc" });
  const [detailId, setDetailId] = useState(null);

  useEffect(() => {
    if (!isAuthed && !localStorage.getItem(DEALER_TOKEN_KEY)) navigate("/dealer/login", { replace: true });
  }, [isAuthed, navigate]);

  useEffect(() => {
    dealerApi.get("/dealer/me").then(({ data }) => setMe(data)).catch(() => {});
  }, []);

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      const { data } = await dealerApi.get("/dealer/claims", { timeout: 40000 });
      setData(data);
      setError("");
    } catch (err) {
      setError(getErrorMessage(err, "Couldn't load the warranty board."));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(false); }, [load]);
  usePolling(useCallback(() => load(true), [load]), 60000);

  const toggleSort = (id) => {
    const col = (data.columns || []).find((c) => c.id === id);
    const defaultDir = col?.type === "date" ? "desc" : "asc"; // dates: newest first; others A→Z / board order
    setSort((s) => (s.id === id ? { id, dir: s.dir === "asc" ? "desc" : "asc" } : { id, dir: defaultDir }));
  };

  const rows = useMemo(() => {
    let r = data.rows || [];
    const q = query.trim().toLowerCase();
    if (q) {
      r = r.filter(
        (row) =>
          (row.chassis || "").toLowerCase().includes(q) || (row.customer || "").toLowerCase().includes(q)
      );
    }
    if (sort.id) {
      const col = (data.columns || []).find((c) => c.id === sort.id);
      const type = col?.type;
      const orderMap = {};
      (col?.status_order || []).forEach((l, i) => { orderMap[l.toLowerCase()] = i; });
      const dir = sort.dir === "asc" ? 1 : -1;
      r = [...r].sort((a, b) => {
        const ra = (a.values?.[sort.id] || "").trim();
        const rb = (b.values?.[sort.id] || "").trim();
        if (!ra && !rb) return 0;
        if (!ra) return 1;   // blanks always sort to the bottom
        if (!rb) return -1;
        if (type === "date") {
          return ((Date.parse(ra) || 0) - (Date.parse(rb) || 0)) * dir;
        }
        if (type === "status") {
          const av = orderMap[ra.toLowerCase()] ?? 9999;
          const bv = orderMap[rb.toLowerCase()] ?? 9999;
          if (av !== bv) return (av - bv) * dir;
          return ra.localeCompare(rb) * dir;
        }
        return ra.localeCompare(rb, undefined, { numeric: true }) * dir;
      });
    }
    return r;
  }, [data.rows, data.columns, query, sort]);

  const columns = data.columns || [];
  const statusColorsByTitle = useMemo(() => {
    const m = {};
    columns.forEach((c) => { if (c.type === "status") m[c.title] = c.status_colors || {}; });
    return m;
  }, [columns]);

  return (
    <div className="min-h-screen w-full bg-[#050505] text-neutral-100">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-[#0F0F0F] border-b border-neutral-800 px-5 sm:px-8 py-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <OtmLogo imgClassName="h-6" showCaravans={false} />
            <div className="hidden sm:block h-8 w-px bg-neutral-800" />
            <div className="flex items-center gap-2">
              <Storefront size={18} className="text-[#73bf44]" weight="duotone" />
              <div>
                <div className="font-heading text-sm font-bold text-white leading-tight" data-testid="dealer-board-name">
                  {me?.name || me?.username || "Dealer Portal"}
                </div>
                <div className="text-[0.65rem] text-neutral-500 uppercase tracking-[0.15em]">Warranty Claims · Read only</div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span
              data-testid="dealer-live"
              title="Auto-refreshes every 60 seconds"
              className="inline-flex items-center gap-1.5 text-[0.7rem] text-neutral-400 border border-neutral-800 rounded-full px-2.5 py-1.5"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#73bf44] pulse-dot" />
              Live{data.fetched_at ? ` · ${fmtTime(data.fetched_at)}` : ""}
            </span>
            <button
              onClick={() => load(true)}
              disabled={refreshing || loading}
              data-testid="dealer-refresh"
              className="inline-flex items-center gap-1.5 border border-neutral-700 text-neutral-200 rounded-full px-3.5 py-1.5 text-sm hover:border-neutral-500 hover:text-white transition-colors disabled:opacity-60"
            >
              <ArrowClockwise size={15} className={refreshing ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              onClick={() => navigate("/")}
              data-testid="dealer-view-site"
              className="hidden sm:inline-flex items-center gap-1.5 border border-neutral-700 text-neutral-400 rounded-full px-3.5 py-1.5 text-sm hover:text-white transition-colors"
            >
              <ArrowSquareOut size={15} /> Site
            </button>
            <button
              onClick={() => { logout(); navigate("/dealer/login"); }}
              data-testid="dealer-logout"
              className="inline-flex items-center gap-1.5 text-red-400 rounded-full px-3 py-1.5 text-sm hover:bg-red-500/10 transition-colors"
            >
              <SignOut size={15} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="px-5 sm:px-8 py-6">
        {/* Controls */}
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div className="flex items-center gap-2 bg-[#0F0F0F] border border-neutral-800 rounded-full px-4 py-2.5 w-full sm:w-96 focus-within:border-[#73bf44] transition-colors">
            <MagnifyingGlass size={16} className="text-neutral-500" />
            <input
              data-testid="dealer-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by chassis number or customer name"
              className="bg-transparent outline-none text-sm text-white w-full placeholder:text-neutral-600"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-neutral-500 mr-1" data-testid="dealer-count">
              {loading ? "Loading…" : `${rows.length} claim${rows.length === 1 ? "" : "s"}`}
            </span>
            <button
              onClick={() => exportCsv(columns, rows, `${(me?.name || me?.username || "dealer")}-warranty-claims-${new Date().toISOString().slice(0, 10)}`)}
              disabled={loading || rows.length === 0}
              data-testid="export-csv"
              className="inline-flex items-center gap-1.5 border border-neutral-700 text-neutral-200 rounded-full px-3.5 py-1.5 text-sm hover:border-[#73bf44] hover:text-[#73bf44] transition-colors disabled:opacity-50"
            >
              <FileCsv size={15} /> CSV
            </button>
            <button
              onClick={() => exportPdf(columns, rows, `${(me?.name || me?.username || "Dealer")} — Warranty Claims`, `${(me?.name || me?.username || "dealer")}-warranty-claims-${new Date().toISOString().slice(0, 10)}`)}
              disabled={loading || rows.length === 0}
              data-testid="export-pdf"
              className="inline-flex items-center gap-1.5 border border-neutral-700 text-neutral-200 rounded-full px-3.5 py-1.5 text-sm hover:border-[#73bf44] hover:text-[#73bf44] transition-colors disabled:opacity-50"
            >
              <FilePdf size={15} /> PDF
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-[#0F0F0F] border border-neutral-800 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center gap-2 text-neutral-500 py-20" data-testid="dealer-board-loading">
              <CircleNotch size={20} className="animate-spin" /> Loading your warranty claims…
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-3 text-neutral-400 py-16" data-testid="dealer-board-error">
              <WarningCircle size={28} className="text-red-400" />
              <p className="text-sm">{error}</p>
              <button onClick={() => load(false)} className="text-sm text-[#73bf44] hover:underline">Try again</button>
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center text-neutral-500 py-16 text-sm" data-testid="dealer-board-empty">
              {query ? "No claims match your search." : "No warranty claims found for your dealership yet."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="dealer-board-table">
                <thead>
                  <tr className="border-b border-neutral-800 bg-black/30">
                    {columns.map((col) => (
                      <th
                        key={col.id}
                        onClick={() => toggleSort(col.id)}
                        data-testid={`dealer-col-${col.id}`}
                        className="text-left font-semibold text-[0.7rem] uppercase tracking-[0.1em] text-neutral-400 px-4 py-3 whitespace-nowrap cursor-pointer select-none hover:text-white transition-colors"
                      >
                        <span className="inline-flex items-center gap-1">
                          {col.title}
                          {sort.id === col.id && (sort.dir === "asc" ? <CaretUp size={11} weight="bold" /> : <CaretDown size={11} weight="bold" />)}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr
                      key={row.id || i}
                      data-testid={`dealer-row-${i}`}
                      onClick={() => setDetailId(row.id)}
                      className="border-b border-neutral-900 hover:bg-white/[0.04] transition-colors last:border-0 cursor-pointer"
                    >
                      {columns.map((col) => (
                        <td key={col.id} className="px-4 py-3 align-top text-neutral-200 whitespace-nowrap max-w-[22rem] truncate">
                          <Cell col={col} value={row.values?.[col.id]} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <p className="text-xs text-neutral-600 mt-4">
          This is a read-only, live view of your warranty claims from On The Move. Click any claim to see full details and documents. It refreshes automatically every 60 seconds.
        </p>
      </main>

      {detailId && <ClaimDetailModal id={detailId} onClose={() => setDetailId(null)} statusColors={statusColorsByTitle} />}
    </div>
  );
}
